/**
 * Deployment processor for Now Assist Skills
 * Handles deployment settings processing including UI Action auto-generation
 * Matches Skill Builder UI patterns for deployment configuration
 */

import type { Record, ObjectShape, Context, Shape } from '@servicenow/sdk-build-core'
import { generateInternalName } from './attribute-processor'

/**
 * Input attribute structure extracted from prompt configuration
 */
export interface InputAttribute {
    name: string
    dataType: string
    mandatory: boolean
    description: string
    tableName: string
}

/**
 * Process UI Action deployment configuration and create sys_ui_action record
 * Matches the defaults from Skill Builder UI (sys_ui_action_c6448c222ff5fa505dce903fafa4e3d9.xml)
 *
 * @param uiActionConfig - UI Action configuration (only table name required)
 * @param skillRecord - The skill record
 * @param capabilityRecord - The capability record (contains capabilityId)
 * @param definitionShape - Definition argument shape containing inputs for script generation
 * @param arg - Argument shape for transform
 * @param factory - Record factory from Context
 * @returns Created UI Action record
 */
export async function processUIActionDeployment(
    uiActionConfig: ObjectShape,
    skillRecord: Record,
    capabilityRecord: Record,
    promptConfig: ObjectShape,
    arg: ObjectShape,
    factory: Context['factory']
) {
    const skillId = skillRecord.getId().getValue()
    const skillName = skillRecord.get('name')?.getValue() || 'Skill'
    const capabilityId = capabilityRecord.getId().getValue()

    // Extract table name for sys_ux_form_action
    const tableName = extractTableName(uiActionConfig)

    // Generate defaults for script and condition
    const defaultScript = generateUIActionScript(skillId, capabilityId, promptConfig)
    const defaultCondition = `new sn_nowassist_admin.NowAssistSkillConfig().isSkillConfigurationActive('${skillId}')`

    // Create sys_ui_action record
    const uiActionRecord = await factory.createRecord({
        source: uiActionConfig,
        table: 'sys_ui_action',
        explicitId: uiActionConfig.asObject().get('$id'),
        properties: uiActionConfig.transform(({ $ }) => ({
            name: $.def(skillName),
            table: $,
            script: $.def(defaultScript).toCdata(),
            condition: $.def(defaultCondition),
            active: $.def(false),
            form_button: $.val(true),
            form_button_v2: $.val(true),
            show_insert: $.val(true),
            show_update: $.val(true),
        })),
    })

    // Create sys_ux_form_action record (Skill Builder also creates this)
    const uxFormActionRecord = await factory.createRecord({
        source: uiActionConfig,
        table: 'sys_ux_form_action',
        properties: arg.transform(({ $ }) => ({
            name: $.val(skillName),
            action_type: $.val('ui_action'),
            table: $.val(tableName),
            ui_action: $.val(uiActionRecord.getId()),
            active: $.val(true),
        })),
    })

    // Return both records so they can be included in the final output
    return {
        uiActionRecord,
        uxFormActionRecord,
    }
}

/**
 * Extract table name from UI Action config
 * Handles ObjectShape format from argument shapes
 */
function extractTableName(uiActionConfig: ObjectShape): string {
    const configObj = uiActionConfig.asObject()

    // Check for 'table' field (primary)
    if (configObj.has('table')) {
        const tableValue = configObj.get('table')
        if (tableValue && tableValue.asString) {
            return tableValue.asString()?.getValue() || ''
        }
    }

    if (configObj.has('tableName')) {
        const tableNameValue = configObj.get('tableName')
        if (tableNameValue && tableNameValue.asString) {
            return tableNameValue.asString()?.getValue() || ''
        }
    }

    return ''
}

/**
 * Generate UI Action script that invokes the skill
 * Auto-detects inputs from the skill definition and maps them to form fields
 */
function generateUIActionScript(skillId: string, capabilityId: string, promptConfig: ObjectShape): string {
    // Extract input fields from prompt configuration
    // promptConfig is an argument shape, need to extract inputs array
    let inputs: InputAttribute[] = []

    if (promptConfig && promptConfig.has && promptConfig.has('inputs')) {
        const inputsArg = promptConfig.get!('inputs')
        if (inputsArg && inputsArg.isArray && inputsArg.isArray()) {
            const inputsArray = inputsArg.asArray()
            inputs = inputsArray.map((inputArg: Shape) => {
                if (inputArg.isObject()) {
                    const inputObj = inputArg.asObject()
                    return {
                        name: inputObj.has('name') ? inputObj.get('name')?.asString()?.getValue() : '',
                        dataType: inputObj.has('dataType')
                            ? inputObj.get('dataType')?.asString()?.getValue()
                            : 'string',
                        mandatory: inputObj.has('mandatory')
                            ? inputObj.get('mandatory')?.asBoolean()?.getValue()
                            : false,
                        description: inputObj.has('description')
                            ? inputObj.get('description')?.asString()?.getValue()
                            : '',
                        tableName: inputObj.has('tableName') ? inputObj.get('tableName')?.asString()?.getValue() : '',
                    }
                }
                return { name: '', dataType: 'string', mandatory: false, description: '', tableName: '' }
            })
        }
    }

    return generateScriptFromInputs(skillId, capabilityId, inputs)
}

/**
 * Generate a UI Action script from a list of InputAttribute objects.
 */
export function generateScriptFromInputs(skillId: string, capabilityId: string, inputs: InputAttribute[]): string {
    // Generate input assignment statements (platform pattern)
    const inputAssignments = inputs
        .filter((input) => input.name)
        .map((input) => {
            const inputName = generateInternalName(input.name)
            const dataType = input.dataType || 'string'

            const tableName = input.tableName || ''

            return generateInputAssignment(inputName, dataType, tableName)
        })
        .join('\n')

    // Generate the script matching the platform's exact format (tab-indented)
    const inputSection = inputAssignments ? `\n${inputAssignments}` : ''
    return `var inputsPayload = {};${inputSection}

var capabilityId = '${capabilityId}'; 
var request = {
\texecutionRequests: [{
\t\tpayload: inputsPayload,
\t\tcapabilityId: capabilityId,
\t\tmeta: {
\t\t\tskillConfigId: '${skillId}'
\t\t}
\t}], 
\tmode: 'sync' 
};

try {
\tvar response = sn_one_extend.OneExtendUtil.executeSecure(request) || {}; 
\tvar skillResponse = ((response["capabilities"] || {})[capabilityId] || {})["response"]; 
\tgs.addInfoMessage(JSON.stringify(skillResponse));
} catch(e) {
\tgs.error(e);
\tgs.addErrorMessage('Something went wrong while executing skill.');
}
action.setRedirectURL(current);`
}

/**
 * Generate an input assignment statement following the platform pattern.
 *
 * Supported data types (from SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE_DATA_TYPES):
 *
 * - **string, numeric, boolean, simple_array, json_object, json_array**:
 *   ```js
 *   // inputName is a input of type: <dataType>
 *   inputsPayload['inputName'] = current.getValue('field_name');
 *   ```
 *
 * - **glide_record**:
 *   ```js
 *   // inputName is a input of type: glide_record
 *   inputsPayload['inputName'] = {
 *     tableName: 'table_name',
 *     sysId: current.getValue('sys_id'),
 *     queryString: ''
 *   };
 *   ```
 *
 * @param inputName - The input name from the skill configuration
 * @param dataType - The data type of the input: string | numeric | boolean | glide_record | simple_array | json_object | json_array
 * @param tableName - The table name (required for glide_record, ignored for other types)
 * @returns Generated JavaScript assignment statement
 */
export function generateInputAssignment(inputName: string, dataType: string, tableName: string): string {
    let assignment = `// ${inputName} is a input of type: ${dataType}\n`
    if (dataType === 'glide_record') {
        assignment += `inputsPayload['${inputName}'] = {\n`
        assignment += `  tableName: '${tableName}',\n`
        assignment += `  sysId: current.getValue('sys_id'),\n`
        assignment += `  queryString: ''\n`
        assignment += `};`
    } else {
        assignment += `inputsPayload['${inputName}'] = current.getValue('${inputName}');`
    }
    return assignment
}
